"use client";

import { useFormatNumber } from "@/hooks/useFormatNumber";
import { slTrustpilotScore } from "@/lib/consts";
import { useMessages } from "@/lib/i18n/useMessages";
import { Launch } from "@mui/icons-material";
import {
  Box,
  Button,
  Rating,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";

interface SLTrustpilotRatingProps {
  /**
   * The Trustpilot URL to link to
   * @default "https://de.trustpilot.com/review/solving.legal"
   */
  trustpilotUrl?: string;
  /**
   * The rating value to display
   * @default slTrustpilotScore from constants
   */
  rating?: number;
  /**
   * Whether to show the rating as compact (smaller size)
   * @default false
   */
  compact?: boolean;
}

export const SLTrustpilotRating = ({
  trustpilotUrl = "https://de.trustpilot.com/review/solving.legal",
  rating = slTrustpilotScore,
  compact = false,
}: SLTrustpilotRatingProps) => {
  const d = useMessages().dialogs;
  const theme = useTheme();
  const formatNumber = useFormatNumber();

  return (
    <Stack
      direction="column"
      p={compact ? 0.5 : 1}
      sx={{
        backgroundColor: theme.palette.background.paper,
        borderRadius: theme.shape.borderRadius,
        border: 2,
        borderColor: theme.palette.secondary.dark,
      }}
    >
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        width="stretch"
        justifyContent="center"
      >
        <Rating
          name="trustpilot-rating"
          value={rating}
          readOnly={true}
          precision={0.5}
          size={compact ? "medium" : "large"}
        />

        <Typography variant="body1" fontWeight={600}>
          {`${formatNumber(rating, { maximumFractionDigits: 1 })} / 5`}
        </Typography>
      </Stack>

      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        width="stretch"
        justifyContent="center"
      >
        <Box>
          <Button
            size="small"
            variant="text"
            href={trustpilotUrl}
            target="_blank"
            rel="noopener noreferrer"
            endIcon={<Launch />}
          >
            {d.appointment.trustpilotLink}
          </Button>
        </Box>
      </Stack>
    </Stack>
  );
};
